import * as angular from "angular";
import template from "./entity-schema-tabs.html";
import "./entity-schema-tabs.less";
import type { ModalTabItem } from "../../../modals/tabbed-modal/tabbed-modal-types";
import { globs } from "../../../../services/globs";
import {
	buildEntitySchemaEntries,
	mergeEntitySchemaEntries
} from "../../entity-schema-state";
import viewModesSection from "../../../view-modes-section";
import entitySchemaViewer from "./components/entity-schema-tab/entity-schema-viewer/entity-schema-viewer";
import entitySchemaEditor from "./components/entity-schema-tab/entity-schema-editor/entity-schema-editor";
import type {
	EntityModel,
	EntityOperation,
	EntityTypeConfig
} from "./entityModalTabTypes";

interface EntitySchemaEntry {
	key: string;
	title: string;
	schema: any;
	value?: any;
	section?: {
		key?: string;
		title: string;
	};
}

interface EntityObjectSchemaEntry extends EntitySchemaEntry {
	section: {
		key?: string;
		title: string;
	};
	value: any;
}

interface EntitySchemaModalTab extends EntityObjectSchemaEntry {
	code: string;
	tab: ModalTabItem;
}

function isObjectSchemaEntry(entry: EntitySchemaEntry): entry is EntityObjectSchemaEntry {
	return !!entry.section;
}

export interface EntitySchemaTabsController {
	entity: EntityModel;
	typeConfig: EntityTypeConfig;
	save: EntityOperation;
}

// eslint-disable-next-line no-redeclare
export class EntitySchemaTabsController {
	public static $inject = ["$sce", "securityService"];

	public entries: EntitySchemaModalTab[] = [];
	public isReadonly = true;
	public saveModel = async(entry: EntitySchemaModalTab, model: any): Promise<any> => {
		const previousModel = entry.value;
		entry.value = angular.copy(model);

		try {
			const entityCandidate = mergeEntitySchemaEntries(this.entity, this.entries);
			return await this.save({ entity: entityCandidate });
		}
		catch(error) {
			entry.value = previousModel;
			throw error;
		}
	};

	public constructor(
		private readonly $sce: angular.ISCEService,
		private readonly securityService
	) {}

	public $onInit(): void {
		this.entries = this.buildObjectEntries(this.entity)
			.map(entry => {
				const code = `SCHEMA_${entry.key.toUpperCase()}`;
				return {
					...entry,
					code,
					tab: {
						name: entry.title,
						code
					}
				};
			});
		this.isReadonly = !this.securityService.isActionAllowed(
			globs.entityTypes.byType(this.typeConfig.Code).write
		);
	}

	public $onChanges(changes: angular.IOnChangesObject): void {
		const entityChange = changes.entity;
		if(!entityChange || entityChange.isFirstChange()) {
			return;
		}

		this.updateEntryValues(entityChange.currentValue);
	}

	private updateEntryValues(entity: EntityModel): void {
		const entriesByKey = new Map(this.buildObjectEntries(entity)
			.map(entry => [entry.key, entry]));

		this.entries.forEach(entry => {
			const nextEntry = entriesByKey.get(entry.key);
			if(nextEntry) {
				entry.value = nextEntry.value;
			}
		});
	}

	private buildObjectEntries(entity: EntityModel): EntityObjectSchemaEntry[] {
		return (buildEntitySchemaEntries(
			this.typeConfig.Schema,
			entity,
			this.$sce
		) as EntitySchemaEntry[]).filter(isObjectSchemaEntry);
	}
}

export default angular
	.module("vesl.components.entity-modal.tabs.entity-schema-tabs", [
		viewModesSection,
		entitySchemaViewer,
		entitySchemaEditor
	])
	.component("entitySchemaTabs", {
		controller: EntitySchemaTabsController,
		template,
		bindings: {
			entity: "<",
			typeConfig: "<",
			save: "&"
		}
	})
	.name;
